import { useEffect, useRef } from "react";

interface AudioWaveformProps {
  stream: MediaStream;
}

export function AudioWaveform({ stream }: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;

    const audioCtx = new AudioContext();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.82;
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let animId: number;

    const draw = () => {
      animId = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      const W = canvas.width;
      const H = canvas.height;
      const mid = H / 2;
      const amplitude = mid * 0.85;
      const sliceWidth = W / bufferLength;

      ctx.clearRect(0, 0, W, H);

      // — Smooth bezier waveform path —
      ctx.beginPath();
      const startY = mid + (dataArray[0] / 128 - 1) * amplitude;
      ctx.moveTo(0, startY);

      for (let i = 0; i < bufferLength - 1; i++) {
        const y0 = mid + (dataArray[i] / 128 - 1) * amplitude;
        const y1 = mid + (dataArray[i + 1] / 128 - 1) * amplitude;
        const x0 = i * sliceWidth;
        const x1 = (i + 1) * sliceWidth;
        ctx.quadraticCurveTo(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      }
      const lastY = mid + (dataArray[bufferLength - 1] / 128 - 1) * amplitude;
      ctx.lineTo(W, lastY);

      // Gradient stroke
      const sg = ctx.createLinearGradient(0, 0, W, 0);
      sg.addColorStop(0, "rgba(239,68,68,0.3)");
      sg.addColorStop(0.15, "rgba(239,68,68,0.9)");
      sg.addColorStop(0.5, "rgba(248,113,113,1)");
      sg.addColorStop(0.85, "rgba(239,68,68,0.9)");
      sg.addColorStop(1, "rgba(239,68,68,0.3)");
      ctx.strokeStyle = sg;
      ctx.lineWidth = 2 * dpr;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.stroke();

      // Fill between waveform and center line
      ctx.lineTo(W, mid);
      ctx.lineTo(0, mid);
      ctx.lineTo(0, startY);
      ctx.closePath();
      const fg = ctx.createLinearGradient(0, 0, 0, H);
      fg.addColorStop(0, "rgba(239,68,68,0.22)");
      fg.addColorStop(0.6, "rgba(239,68,68,0.06)");
      fg.addColorStop(1, "rgba(239,68,68,0)");
      ctx.fillStyle = fg;
      ctx.fill();

      // Subtle center line
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(W, mid);
      ctx.strokeStyle = "rgba(239,68,68,0.12)";
      ctx.lineWidth = 1 * dpr;
      ctx.stroke();
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      source.disconnect();
      void audioCtx.close();
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-16 rounded-xl bg-red-50/40 dark:bg-red-950/20"
    />
  );
}
